import type { V2AdminCreditDetailDto } from '@idc-forms/api-client'

type CreditStatus = V2AdminCreditDetailDto['status']

export type CreditDocumentAccess = {
  /** Renombrar y eliminar. Ver y descargar NO pasan por acá: la lectura no se apaga nunca. */
  canWrite: boolean
  /** Qué decirle a quien no puede escribir. Se pinta en el tooltip de la acción apagada. */
  writeBlockedReason: string
}

/**
 * ¿Qué se puede hacer con los documentos de ESTE expediente, para ESTA persona?
 *
 * ── POR QUÉ ES UNA FUNCIÓN Y NO DOS `const` EN CADA PANTALLA ─────────────────
 *
 * Porque son dos pantallas —el campo del checklist y la pestaña «Documentos»— y una regla sola.
 * Escrita dos veces, la primera que cambie deja a la otra prometiendo algo distinto sobre el mismo
 * expediente; y como el servidor aplica la suya igual, el síntoma no sería una pantalla más
 * permisiva sino un 409 que aparece recién al apretar el botón.
 *
 * ── LA REGLA, TAL CUAL LA FIJÓ EL NEGOCIO ────────────────────────────────────
 *
 * **Lectura siempre; escritura sólo mientras el trámite está en proceso.** No hay reapertura: al
 * aprobar, los documentos quedan congelados de forma definitiva. Por eso el motivo se redacta en
 * pasado y sin salida («quedaron congelados») en vez de sugerir un permiso o un pedido que no
 * existe.
 *
 * ☠️ **El estado gana al permiso, y ese orden importa.** Con el trámite cerrado, tener
 * `ManageCreditDocuments` no destraba nada: decir «te falta un permiso» mandaría a alguien a
 * pedirle a su jefe una llave que no abre esa puerta. El permiso sólo se menciona cuando es lo
 * único que falta.
 */
export function creditDocumentAccess(
  status: CreditStatus,
  canManageDocuments: boolean
): CreditDocumentAccess {
  if (status === 'approved') {
    return {
      canWrite: false,
      writeBlockedReason: 'Este trámite ya se aprobó: sus documentos quedaron congelados.'
    }
  }
  if (status === 'cancelled') {
    return {
      canWrite: false,
      writeBlockedReason: 'Este trámite se canceló: sus documentos quedaron congelados.'
    }
  }
  return {
    canWrite: canManageDocuments,
    writeBlockedReason:
      'Necesitas el permiso ManageCreditDocuments para modificar los documentos de este expediente.'
  }
}
